import React, { ReactNode, useState } from "react";
import { Input } from "~/components/Controls/Input";
import { DropDown } from "~/components/DropDown";
import { IWithPopper } from "~/components/Popper";
import { QueryList } from "~/components/QueryList";
import { Intent } from "~/lib/intent";
import style from "~/components/Controls/Select/index.module.scss";

export interface ISelectItem<T> {
    label: string;
    value: T;
}

export interface ISelectGroup<T> {
    label: string;
    items: ISelectItem<T>[];
}

export interface IGroupedSelectProps<T> extends IWithPopper {
    groups: ISelectGroup<T>[];
    value?: T | null;
    onChange: (value: T | null) => void;
    onQueryChange?: (query: string) => void;
    intent?: Intent;
    placeholder?: string;
    disabled?: boolean;
    searchable?: boolean;
    clearable?: boolean;
    rightIcon?: ReactNode;
    className?: string;
}

export const GroupedSelectInput = <T extends any>({
    groups,
    value,
    onChange,
    onQueryChange,
    placeholder = "Select…",
    disabled,
    searchable = true,
    clearable,
    rightIcon,
    className,
    intent,
    ...props
}: IGroupedSelectProps<T>) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");

    const allItems = groups.flatMap((g) => g.items);
    const selectedItem = allItems.find((i) => i.value === value);

    const handleQueryChange = (q: string) => {
        setQuery(q);
        onQueryChange?.(q);
    };

    return (
        <DropDown
            {...props}
            disabled={disabled}
            isOpen={open}
            onToggle={setOpen}
            className={[style.select, className].join(" ")}
            intent={intent}
            rightIcon={rightIcon}
            content={() => (
                <>
                    {searchable && !disabled && (
                        <div className={style.selectQuery}>
                            <Input
                                value={query}
                                onChange={handleQueryChange}
                                rightIcon={<i className="fa fa-search" />}
                                autoFocus
                            />
                        </div>
                    )}
                    <div className={style.selectItems}>
                        {groups.map((group, gi) => {
                            const filtered = query
                                ? group.items.filter((item) =>
                                    item.label.toLowerCase().includes(query.toLowerCase())
                                  )
                                : group.items;

                            const showEmptyMessage = filtered.length === 0;
                            const isOtherDomains = group.label.toLowerCase().includes("other");

                            return (
                                <div key={gi} className={style.selectGroup}>
                                    <div className={style.selectGroupLabel}>{group.label}</div>
                                    {!showEmptyMessage ? (
                                        <QueryList
                                            items={filtered}
                                            onSelect={(item) => {
                                                onChange(item.value);
                                                setOpen(false);
                                                setQuery("");
                                            }}
                                            filter={(item) =>
                                                item.label.toLowerCase().includes(query.toLowerCase())
                                            }
                                            emptyRenderer={null}
                                            itemRenderer={({ onClick, item, index }) => (
                                                <div
                                                    onClick={onClick}
                                                    className={style.selectItem}
                                                    key={index}
                                                >
                                                    {item.label}
                                                </div>
                                            )}
                                        />
                                    ) : (
                                        <div className={style.selectGroupEmpty}>
                                            {isOtherDomains
                                                ? (group.items.length === 0 && !query)
                                                    ? "Start typing to search domains"
                                                    : query
                                                        ? "No domains found"
                                                        : "Start typing to search domains"
                                                : `No ${group.label.toLowerCase()}`}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        >
            <div className={style.selectCurrentItem}>
                {selectedItem ? (
                    <>
                        {selectedItem.label}
                        {clearable && !disabled && (
                            <i
                                className={["fa fa-close", style.selectClear].join(" ")}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onChange(null);
                                }}
                            />
                        )}
                    </>
                ) : (
                    <span className={style.selectPlaceholder}>{placeholder}</span>
                )}
            </div>
        </DropDown>
    );
};
